import { Fragment, useState, useContext, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { ROTextField, ROAutocomplete, BaseAutocomplete } from 'shared-components/components/FormFields';
import ROPatientCarePlanPageFooter from '../Footer';
import { VerticalListState, CarePlanTemplateState, VerticalListType, SigmaResponseData } from './TemplatePageInterface';

import { getOptionByValue } from '../DiagnosisPages/Utils';
import { Logger, logPage } from 'shared-components/utils';
import {
  CareplanListFilter,
  SiteGroupType,
  DEFAULT_SITEGROUP,
  DEFAULT_SITE,
  CarePlanActionType,
  ROPatientCarePlanRoute,
} from '../Interface';
import ROPatientCarePathSidePanel from '../SidePanel/SidePanel';
import { CAREPLAN_PAGES } from '../Constants';
import ROPatientCarePlanContext from '../ROPatientCarePlanContext';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { gql } from '@apollo/client';
import {
  CREATE_PRESCRIPTION_FROM_TEMPLATE,
  CREATE_PRESCRIPTION_FROM_MANUAL_SITE,
  TEMPLATE_FILTER_LIST,
  LIST_CAREPLAN_FILTERS,
  LIST_TUMOUR_STREAMS,
  LIST_TREATMENT_SITES_ORDERBY_USAGE,
  GET_TUMOUR_STREAM_FROM_CAREPLAN,
} from './TemplatePageQueries';
import { GET_PEER_REVIEW } from '../SidePanel/SidePanelQueries';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import {
  GET_CAREPLAN_SITE_GROUPS,
  GET_CAREPLAN,
  FREQUENTLY_USED_TEMPLATES,
  SIGMA_DATA,
  GET_CAREPLAN_PRESCRIPTIONS,
} from '../Queries';
import './TemplatePage.scss';
import { alphabeticalOrder, CleanSpecifyFromSiteName, isSpecifySite } from '../Common';
import { validateField, isPageValid } from '../ValidationEngine';
import { CareplanBanners } from '../Banner/Banner';
import { useWindowSize } from 'shared-components/utils/CustomHooks';
import { LoadingSpinner, StandardDialog } from 'shared-components/components';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar/NotificationSnackbar';
import { Alert } from '@mui/material';
import CareplanContainer from '../Container';
import { Features, UserContext } from 'op-contexts';
import Sigma from './SuggestedTemplates/Sigma';
import FrequentlyUsed from './SuggestedTemplates/FrequentlyUsed';
import { LOG_SIGMA_MUTATION } from '../DiagnosisPages/Queries';
import { SEARCH_DROPDOWN_PLACEHOLDER } from 'shared-components/components/FormFields/common';
import { logMissingDiagnosisCode } from '../Common';
import { Box, useTheme, InputAdornment, Typography, Stack, ToggleButton, Grid, Button } from '@mui/material';
import { Search, Clear, CloseOutlined } from '@mui/icons-material';
import { BaseTextField, SelectOptionType } from 'shared-components/components/FormFields';
import TemplateSelectionTable from './TemplateSelectionTable';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';

const MAX_TREATMENT_SITE_OPTIONS_LENGTH = 8;
const UROGENITAL_TS = 'urogenital';
const BREAST_TS = 'breast';
const LUNG_TS = 'lung';

let pressedContinue = false;
const isFieldValid = (field: any) => {
  return pressedContinue ? validateField(field) : true;
};
const logger = new Logger('ROPatientTemplateSelectionPage');
const getNewSiteGroupTemplate = (treatmentSite: string, laterality: string): SiteGroupType => ({
  ...DEFAULT_SITEGROUP,
  sites: [
    {
      ...DEFAULT_SITE,
      treatmentSite,
      laterality,
    },
  ],
});

const sortOptionType = (a: SelectOptionType, b: SelectOptionType) => {
  if (a.value < b.value) return -1;
  if (a.value > b.value) return 1;
  return 0;
};
export const getVerticalListState = (verticalList: VerticalListType[]): VerticalListState[] =>
  verticalList.map(
    (option: VerticalListType): VerticalListState => ({
      ...option,
      selected: '',
      show: option.id === 'site',
    }),
  );

const ROPatientCarePathTemplatePage = (): JSX.Element => {
  const windowSize = useWindowSize();
  const isSmallDevice = windowSize.width <= 1440;
  const client = useApolloClient();
  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const { careplanId, id: patientId } = match.params;
  const pageLoadTime = useRef(new Date().getTime());
  const { state: userState, hasFeature, isFeatureLoading } = useContext(UserContext);
  const [sigmaActive, setSigmaActive] = useState(false);
  const theme = useTheme();
  const [createPrescriptionFromManualSite] = useMutation(CREATE_PRESCRIPTION_FROM_MANUAL_SITE, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
    ],
  });
  const [createPrescriptionFromTemplate, { loading: createPrescriptionFromTemplateLoading }] = useMutation(
    CREATE_PRESCRIPTION_FROM_TEMPLATE,
    {
      refetchQueries: [
        { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
        { query: GET_CAREPLAN, variables: { id: careplanId } },
        { query: GET_PEER_REVIEW, variables: { careplanId } },
      ],
    },
  );
  const [logEvent] = useMutation(LOG_SIGMA_MUTATION);
  const [tumourStream, setTumourStream] = useState<string>('');
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string>('');
  const [selectedDoseFilter, setSelectedDoseFilter] = useState<string>('');
  const [selectedTechniqueFilter, setSelectedTechniqueFilter] = useState<string>('');
  const [siteFilters, setSiteFilters] = useState<string[]>([]);
  const [doseFilters, setDoseFilters] = useState<string[]>([]);
  const [techniqueFilters, setTechniqueFilters] = useState<string[]>([]);
  const [careplans, setCareplans] = useState<CareplanListFilter[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [loadingTemplateId, setLoadingTemplateId] = useState<number | null>(null);

  const showSnackbarNotification = useCallback(
    (action: 'Added' | 'Deleted' | 'Failed to add' | 'Failed to delete', itemName: string) => {
      setSnackbarMessage(`${action} protocol "${itemName}"`);
      setSnackbarOpen(true);
    },
    [],
  );

  const [pageState, setState] = useState<CarePlanTemplateState>({
    siteGroupTemplatesState: [],
    modalShow: false,
    treatmentSites: '',
    laterality: '',
    location: '',
    validated: false,
    tumourStreamList: [],
    treatmentSiteList: [],
    selectedCareplanList: [],
    frequentlyUsedTemplates: [],
    sigmaResponse: undefined,
  });
  const { dispatch } = useContext(ROPatientCarePlanContext);
  const history = useHistory();

  // May be better to pass this in, many of the other calls have to wait for the tumourSteam that this returns
  const { loading: tumourStreamLoading, data: tumourData } = useQuery(GET_TUMOUR_STREAM_FROM_CAREPLAN, {
    variables: { careplanId },
    onCompleted: (data) => {
      logMissingDiagnosisCode(logger, data.careplan, 'Template Information');
    },
  });
  const { data: tumourStreamData } = useQuery(LIST_TUMOUR_STREAMS);
  const { data: treatmentSitesData } = useQuery(LIST_TREATMENT_SITES_ORDERBY_USAGE);
  const { data: templateFilterListData } = useQuery(TEMPLATE_FILTER_LIST, {
    variables: {
      tumourStream,
      siteFilter: selectedSiteFilter,
      doseFilter: selectedDoseFilter,
      techniqueFilter: selectedTechniqueFilter,
      patientId,
    },
    skip: !tumourStream,
  });
  const { data: currentPrescriptions } = useQuery(GET_CAREPLAN_PRESCRIPTIONS, {
    variables: { careplanId },
  });

  useQuery(FREQUENTLY_USED_TEMPLATES, {
    variables: { patientId, tumourStream },
    skip: !tumourStream,
    fetchPolicy: 'network-only',
    onCompleted: (data): void =>
      setState((prevState) => {
        return { ...prevState, frequentlyUsedTemplates: data?.frequentlyUsedTemplates };
      }),
  });

  interface SigmaVariables {
    careplanId: string;
  }

  const { loading: sigmaLoading, data: sigmaData } = useQuery<SigmaResponseData, SigmaVariables>(SIGMA_DATA, {
    variables: { careplanId },
    skip: !sigmaActive,
    fetchPolicy: 'network-only',
    onError: (error): void => {
      logger.info(error?.message);
    },
  });

  const [searchTerm, setSearchTerm] = useState('');
  const firstUpdate = useRef(true);
  const { data: careplan } = useQuery(GET_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { id: careplanId },
  });

  const { data: careplanFiltersData } = useQuery(LIST_CAREPLAN_FILTERS, {
    variables: {
      patientId,
      tumourStream,
      siteFilter: selectedSiteFilter ? selectedSiteFilter : null,
      doseFilter: selectedDoseFilter ? selectedDoseFilter : null,
      techniqueFilter: selectedTechniqueFilter ? selectedTechniqueFilter : null,
      search: searchTerm,
    },
    fetchPolicy: 'network-only',
    skip: !tumourStream || !(searchTerm || selectedSiteFilter || selectedDoseFilter || selectedTechniqueFilter),
  });

  const [, updateState] = useState();
  //@ts-ignore
  const forceUpdate = useCallback(() => updateState({}), []);

  useEffect(() => {
    let tumourOptionType = [];
    let treatmentSiteList = [];
    let selectedCareplanList = [];

    if (tumourStreamData) {
      tumourOptionType = tumourStreamData.tumourStreamList.map((stream: string): SelectOptionType => {
        return { label: stream, value: stream };
      });
    }
    if (treatmentSitesData) {
      treatmentSiteList = treatmentSitesData.mostUsedSiteReferenceList.map((site: any): SelectOptionType => {
        return { label: site.treatmentSite, value: site.treatmentSite };
      });
    }
    if (currentPrescriptions) {
      selectedCareplanList = currentPrescriptions.prescriptionsByCareplan.map((prescription: any) => {
        return {
          id: prescription.id,
          template: prescription,
          name: prescription.name,
          ccTemplate: prescription.template?.template,
        };
      });
    }
    setState({
      ...pageState,
      tumourStreamList: tumourOptionType,
      treatmentSiteList,
      selectedCareplanList,
    });
  }, [tumourStreamData, treatmentSitesData, currentPrescriptions]);

  useEffect(() => {
    if (careplanFiltersData) {
      setCareplans(careplanFiltersData.careplanTemplateList);
    }
  }, [careplanFiltersData]);

  useEffect(() => {
    if (templateFilterListData) {
      setSiteFilters(templateFilterListData.templateFilterList.siteFilter);
      setDoseFilters(templateFilterListData.templateFilterList.doseFilter);
      setTechniqueFilters(templateFilterListData.templateFilterList.techniqueFilter);
    }
  }, [templateFilterListData]);

  useEffect(() => {
    if (!createPrescriptionFromTemplateLoading) {
      setLoadingTemplateId(null);
    }
  }, [createPrescriptionFromTemplateLoading]);

  useEffect(() => {
    if (tumourData && !tumourStream) {
      setTumourStream(tumourData.careplan?.diagnosis?.diagnosisCode?.tumourStream?.name || '');
    }
  }, [tumourData]);

  useLayoutEffect(() => {
    if (searchTerm !== '') {
      client
        .query({
          query: gql`
            query careplanTemplateList($tumourStream: String!, $search: String, $patientId: ID!) {
              careplanTemplateList(tumourStream: $tumourStream, search: $search, patientId: $patientId) {
                id
                name
                rules {
                  id
                  field
                  defaultValue
                }
              }
            }
          `,
          variables: {
            tumourStream,
            search: searchTerm,
            patientId: patientId,
          },
        })
        .then((careplanData: any) => {
          const careplans: any = careplanData.data.careplanTemplateList;
          if (careplans) {
            setSelectedSiteFilter('');
            setSelectedDoseFilter('');
            setSelectedTechniqueFilter('');
            setCareplans(careplans);
          }
        });
    } else if (searchTerm === '' && !firstUpdate.current) {
      setState((prevState) => {
        return { ...prevState, careplans: [] };
      });
    } else firstUpdate.current = false;
  }, [searchTerm]);

  useEffect(() => {
    pressedContinue = false;
  }, []);
  const sigmaTumourStreams = [BREAST_TS, UROGENITAL_TS, LUNG_TS];

  useEffect(() => {
    setSigmaActive(
      sigmaTumourStreams.includes(tumourStream.toLowerCase()) &&
        Boolean(hasFeature(Features.SIGMA)) &&
        tumourData?.careplan?.diagnosis.isPrimaryDiagnosis,
    );
  }, [userState.features, tumourStream, tumourData]);

  // clear search term when one of the filters is changed
  useEffect(() => {
    if (selectedSiteFilter || selectedDoseFilter || selectedTechniqueFilter) {
      setSearchTerm('');
    }
  }, [selectedSiteFilter, selectedDoseFilter, selectedTechniqueFilter]);

  // On page refresh user features take a moment to load, causing flicker of frequently used templates
  if (isFeatureLoading() || (sigmaActive && sigmaLoading) || tumourStreamLoading)
    return (
      <LoadingSpinner
        loadingText={'Loading Careplan Selection'}
        subtitle={'Please wait while we set things up for you'}
      />
    );

  const selectCareplan = (
    treatmentSite: string,
    careplanName: string,
    templateId: number,
    treatmentSiteSpecify: string,
    selectionMethod?: string,
  ): void => {
    const nextSiteGroupTemplatesState = getNewSiteGroupTemplate(treatmentSite, '');
    const elem = document.querySelector(`[data-testid="${careplanName}"]`) as any;
    const isManualSelection = selectionMethod === 'manual';
    const manualSiteName = treatmentSiteSpecify
      ? `${CleanSpecifyFromSiteName(`${treatmentSite} (${treatmentSiteSpecify})`)}`
      : treatmentSite;
    const displayName = isManualSelection ? manualSiteName : careplanName;

    if (!isManualSelection) {
      setLoadingTemplateId(templateId);
    }

    if (elem && isManualSelection) {
      if (elem.hasAttribute('disabled')) return;
      elem.setAttribute('disabled', '');
      elem.classList.add('active');

      setTimeout(() => {
        setSelectedSiteFilter('');
        setSelectedDoseFilter('');
        setSelectedTechniqueFilter('');
        setCareplans([]);
        setState({
          ...pageState,
          siteGroupTemplatesState: [...pageState.siteGroupTemplatesState, nextSiteGroupTemplatesState],
        });
      }, 0);
    }

    setState({
      ...pageState,
      siteGroupTemplatesState: [...pageState.siteGroupTemplatesState, nextSiteGroupTemplatesState],
    });
    dispatch({
      type: CarePlanActionType.SET_CAREPLAN_SITES,
      payload: { template: { name: careplanName, groups: [nextSiteGroupTemplatesState] } },
    });
    if (isManualSelection) {
      createPrescriptionFromManualSite({
        variables: {
          careplanId,
          treatmentSite,
          treatmentSiteSpecify,
        },
      })
        .then(() => {
          showSnackbarNotification('Added', displayName);
        })
        .catch((error) => {
          logger.error('Manual site creation failed:', error);
          showSnackbarNotification('Failed to add', displayName);
        });
    } else {
      createPrescriptionFromTemplate({
        variables: {
          careplanId,
          templateId,
          selectionMethod,
        },
      })
        .then(() => {
          showSnackbarNotification('Added', displayName);
          setLoadingTemplateId(null);
        })
        .catch((error) => {
          logger.error('Template creation failed:', error);
          showSnackbarNotification('Failed to add', displayName);
          setLoadingTemplateId(null);
        });
    }
  };
  const hideModal = (): void => {
    pressedContinue = false;
    setState({ ...pageState, modalShow: false });
  };
  const validationDependencyStructure = [
    {
      parent: null,
      expansionValue: null,
      children: ['treatmentSites'],
    },
    {
      parent: 'isSpecify',
      expansionValue: true,
      children: ['location'],
    },
  ];
  const isManualSiteValid = () => {
    // Create an isSpecify flag so we can use it in the validation structure
    const stateWithSpecify = { ...pageState, isSpecify: isSpecifySite(pageState.treatmentSites) };
    return isPageValid(validationDependencyStructure, stateWithSpecify);
  };

  const getSigmaStatus = (): string => {
    // Breast only for MVP. When extended to other streams may need more logic around tumour endpoint availablity
    const diagnosisCode = tumourData?.careplan?.diagnosis?.diagnosisCode;
    const tumourStream = diagnosisCode?.tumourStream?.name.toLowerCase();

    if (diagnosisCode && !sigmaTumourStreams.includes(tumourStream)) {
      return 'not_supported'; // No sigma endpoint available
    } else if (tumourStream === UROGENITAL_TS && diagnosisCode.diagnosisCode !== 'C61') {
      // If the tumour stream is Urogenital and the diagnosis code is not C61, then sigma is not support
      return 'not_supported';
    } else {
      if (sigmaActive) {
        switch (sigmaData?.sigmaData?.status) {
          case '200':
            return 'ok';
          case '422':
            return 'validation_error';
          default:
            return 'unexpected_error';
        }
      }
      return 'inactive'; // tumour stream available for Sigma Suggestions, Sigma not active for user
    }
  };

  const usageMetricsData = {
    event_name: 'careplans_selected',
    practitioner_id: parseInt(tumourData.careplan.practitioner.id),
    patient_id: parseInt(patientId),
    diagnosis_id: parseInt(tumourData.careplan.diagnosis.id),
    careplan_id: parseInt(tumourData.careplan.id),
    sigma_response_uuid: sigmaData?.sigmaData?.responseUUID,
    sigma_status: getSigmaStatus(),
    selected_templates: pageState.selectedCareplanList.map((selectedCareplan) => {
      return {
        id: selectedCareplan.ccTemplate?.id ?? selectedCareplan.template.id, //manual careplans don't have a carepath template
        name: selectedCareplan.template.name,
        selectionMethod: selectedCareplan.template.selectionMethod,
      };
    }),
    recommended_templates: sigmaData?.sigmaData?.templates?.map((template) => {
      return {
        id: template.id,
        name: template.name,
        score: template.score,
      };
    }),
  };

  const endAdornment = () => {
    if (searchTerm) {
      return (
        <InputAdornment position="end">
          <Clear sx={{ cursor: 'pointer' }} onClick={() => setSearchTerm('')} color="primary" />
        </InputAdornment>
      );
    }
    return null;
  };

  return (
    <Fragment>
      <div className="main-container-parent-wrapper">
        <div className="main-container-wrapper">
          <CareplanBanners data={careplan} />
          <CareplanContainer className={'template-selection-page carepath-template-page'}>
            <div className="page-header">
              <Typography variant="h5" data-test-id="template-select-header">
                Careplan Selection
              </Typography>
              <Typography color={theme.palette.text.secondary} paddingTop={1} variant="body1">
                Please select careplan templates/sites.
              </Typography>
            </div>
            {sigmaActive && sigmaData?.sigmaData?.templates?.length ? (
              <Sigma
                templates={sigmaData?.sigmaData?.templates}
                selectCareplan={selectCareplan}
                tumourStream={tumourStream}
              />
            ) : (
              <FrequentlyUsed
                templates={pageState.frequentlyUsedTemplates}
                selectCareplan={selectCareplan}
                tumourStream={tumourStream}
              />
            )}

            <div className="freq-search-separator"></div>
            <div className="cp-select-ddsearch-wrapper">
              <div className="cp-select-ddsearch">
                <div className="cp-select-ddsearch">
                  <Typography width={'170px'} variant="h6" textTransform="capitalize">
                    {`${CurrentAppConfig.DefaultValue.tumourName} Stream`}
                  </Typography>
                  <div style={{ width: '360px', display: 'flex', gap: '8px' }}>
                    <BaseAutocomplete
                      id="tumourStream"
                      value={getOptionByValue(pageState.tumourStreamList, tumourStream)}
                      disableClearable
                      onChange={(option: SelectOptionType | string): void => {
                        const value = typeof option === 'string' ? option : option?.value;
                        if (tumourStream !== value) {
                          setSearchTerm('');
                          setSelectedSiteFilter('');
                          setSelectedDoseFilter('');
                          setSelectedTechniqueFilter('');
                          setDoseFilters([]);
                          setTechniqueFilters([]);
                          setSiteFilters([]);
                          setCareplans([]);
                          setTumourStream(value);
                        }
                      }}
                      options={pageState.tumourStreamList}
                    />
                    {(selectedSiteFilter || selectedDoseFilter || selectedTechniqueFilter) && (
                      <Button
                        variant="outlined"
                        sx={{
                          minWidth: '120px',
                          padding: '4px 8px',
                        }}
                        onClick={() => {
                          setSelectedSiteFilter('');
                          setSelectedDoseFilter('');
                          setSelectedTechniqueFilter('');
                          setDoseFilters([]);
                        }}>
                        Clear filters
                      </Button>
                    )}
                  </div>
                </div>
                <div className="add-cp-site-manually">
                  <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer' }}
                    color={theme.palette.primary.dark}
                    onClick={(): void => {
                      setState({ ...pageState, modalShow: true, location: '', treatmentSites: '' });
                    }}>
                    {isSmallDevice ? (
                      'Add site manually'
                    ) : (
                      <>
                        Can't find template? <br /> Add site manually
                      </>
                    )}
                  </Typography>
                </div>
                <div className="cp-search-input-wrapper">
                  <BaseTextField
                    name="cpTemplateSearchInput"
                    id="cpTemplateSearchInput"
                    type="search"
                    placeholder="Search careplan templates…"
                    fullWidth
                    value={searchTerm}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchTerm(event.target.value);
                      setSelectedSiteFilter('');
                      setSelectedDoseFilter('');
                      setSelectedTechniqueFilter('');
                    }}
                    InputProps={{
                      startAdornment: <Search color="primary" />,
                      endAdornment: endAdornment(),
                    }}
                    sx={{
                      marginLeft: '16px',
                    }}
                  />
                </div>
              </div>
            </div>
            <TemplateSelectionTable
              searchTerm={searchTerm}
              siteFilter={selectedSiteFilter}
              doseFilter={selectedDoseFilter}
              techniqueFilter={selectedTechniqueFilter}
              setSiteFilter={setSelectedSiteFilter}
              setDoseFilter={setSelectedDoseFilter}
              setTechniqueFilter={setSelectedTechniqueFilter}
              treatingDepartment={careplan?.careplan?.treatingDepartment}
              siteFilterOptions={alphabeticalOrder(siteFilters, null)}
              doseFilterOptions={alphabeticalOrder(doseFilters, null)}
              techniqueFilterOptions={alphabeticalOrder(techniqueFilters, null)}
              setCareplan={(id, name) => selectCareplan(tumourStream, name, id, '', 'filter')}
              careplanOptions={alphabeticalOrder(careplans, 'name')}
              setCareplanOptions={setCareplans}
              loadingTemplateId={loadingTemplateId}
            />
          </CareplanContainer>
          <ROPatientCarePlanPageFooter
            onBack={(): void => {
              history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.TREATMENT_INFO}`);
            }}
            onNext={(): void => {
              logPage(pageLoadTime.current, usageMetricsData, logEvent);
              history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.SITESETUP}`);
            }}
            nextDisabled={
              !(
                currentPrescriptions &&
                currentPrescriptions.prescriptionsByCareplan &&
                currentPrescriptions.prescriptionsByCareplan.length !== 0
              )
            }
          />
        </div>
        <ROPatientCarePathSidePanel
          onPrescriptionDeleted={(prescriptionName) => showSnackbarNotification('Deleted', prescriptionName)}
          onPrescriptionDeleteFailed={(prescriptionName) =>
            showSnackbarNotification('Failed to delete', prescriptionName)
          }
        />
      </div>
      <StandardDialog
        id="treatment-sites-modal"
        open={pageState.modalShow}
        maxWidth="lg"
        title="Add Treatment Site Manually"
        onClose={hideModal}
        submitText="Add"
        showDividers
        enableActionPopover
        onSubmit={() => {
          pressedContinue = true;
          forceUpdate();
          if (!isManualSiteValid()) return;
          selectCareplan(pageState.treatmentSites, '', -1, pageState.location, 'manual');

          hideModal();
        }}>
        <Stack>
          <Typography variant="subtitle1">Most Common Sites</Typography>
          <Grid container spacing={2} paddingY={3} data-testid="treatment-sites-buttons">
            {pageState.treatmentSiteList.slice(0, MAX_TREATMENT_SITE_OPTIONS_LENGTH).map(
              (option: SelectOptionType): JSX.Element => (
                <Grid item xs={3} key={`treatment-sites-${option.value}`}>
                  <ToggleButton
                    fullWidth
                    selected={pageState.treatmentSites === option.value}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                      paddingX: 1,
                    }}
                    key={`treatment-sites-${option.value}`}
                    value={option.value}
                    onClick={(): void => {
                      setState({ ...pageState, treatmentSites: option.value, location: '' });
                    }}>
                    <Typography variant="body2">{option.label}</Typography>
                  </ToggleButton>
                </Grid>
              ),
            )}
          </Grid>
          <Typography paddingY={2} variant="subtitle1">
            Search & Select
          </Typography>
          <Box component="form" id="addSiteManuallyForm">
            <ROAutocomplete
              id="treatmentSites"
              fieldlabel="Treatment Sites"
              placeholder={SEARCH_DROPDOWN_PLACEHOLDER}
              options={pageState.treatmentSiteList.sort(sortOptionType)}
              value={getOptionByValue(pageState.treatmentSiteList, pageState.treatmentSites)}
              onChange={(option: SelectOptionType | string): void => {
                const value = typeof option === 'string' ? option : option?.value;
                setState({
                  ...pageState,
                  treatmentSites: value || '',
                  location: '',
                });
              }}
              inputProps={{
                error: !isFieldValid(pageState.treatmentSites),
                helperText: !isFieldValid(pageState.treatmentSites) ? 'This field is required' : undefined,
              }}
              required
              disableClearable
            />
            {isSpecifySite(pageState.treatmentSites) && (
              <ROTextField
                id="location"
                fieldlabel="Location"
                value={pageState.location}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  setState({ ...pageState, location: event.target.value });
                }}
                error={!isFieldValid(pageState.location)}
                helperText={!isFieldValid(pageState.location) ? 'This field is required' : undefined}
                required
              />
            )}
          </Box>
        </Stack>
      </StandardDialog>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        height="180px"
        type="notification"
      />
    </Fragment>
  );
};

export default ROPatientCarePathTemplatePage;
